import { ADULTS_KEY, PARTICIPATIONS, STAGE_KEYS, type AudienceKey } from '@yoyoclub/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CheckboxMenu,
  CheckboxSection,
  Chip,
  FilterButton,
  OptionMenu,
  RadioSection,
} from '../activities/filterControls';
import { AUDIENCE_THEME, HOME_REGIONS, HOME_TIMES, type HomeTime } from '../home/homeState';

import { EMPTY_PICKS_FILTERS, PICK_LANGUAGES, type PicksFilterState } from './picksFilterState';

type ListKey = 'regions' | 'categories' | 'languages' | 'participations';
type Menu = null | 'when' | 'age' | ListKey;

const toggled = (list: string[], v: string) =>
  list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

/** Option labels shared by the bar and the sheet. */
function useOptions(categories: string[]) {
  const { t } = useTranslation(['activities', 'home', 'common', 'picks']);
  const ageLabel = (a: AudienceKey) =>
    a === ADULTS_KEY
      ? t('home:selector.parents')
      : `${AUDIENCE_THEME[a].age} ${t('home:selector.yearsUnit')}`;
  return {
    t,
    when: HOME_TIMES.map((w) => ({ value: w, label: t(`home:filters.times.${w}`) })),
    age: [
      { value: 'all', label: t('picks:list.all') },
      ...[...STAGE_KEYS, ADULTS_KEY].map((a) => ({ value: a, label: ageLabel(a) })),
    ],
    ageLabel,
    lists: {
      regions: HOME_REGIONS.map((r) => ({ value: r, label: t(`common:region.${r}`) })),
      categories: categories.map((c) => ({ value: c, label: t(`common:category.${c}`) })),
      languages: PICK_LANGUAGES.map((l) => ({
        value: l,
        label: l === 'NA' ? t('picks:submit.languageNA') : t(`common:teachingLanguage.${l}`),
      })),
      participations: PARTICIPATIONS.map((p) => ({
        value: p,
        label: t(`common:participation.${p}`),
      })),
    } as Record<ListKey, { value: string; label: string }[]>,
    listTitle: {
      regions: t('activities:filters.region'),
      categories: t('activities:filters.category'),
      languages: t('activities:filters.language'),
      participations: t('activities:filters.participation'),
    } as Record<ListKey, string>,
  };
}

const LIST_KEYS: ListKey[] = ['regions', 'categories', 'languages', 'participations'];

/**
 * The /picks filter bar in the All Activities look (pill buttons with dropdowns and
 * removable chips). Phones use <PicksFiltersSheet/>.
 */
export function PicksFilters({
  value,
  categories,
  onChange,
}: {
  value: PicksFilterState;
  categories: string[];
  onChange: (next: PicksFilterState) => void;
}) {
  const o = useOptions(categories);
  const { t } = o;
  const [open, setOpen] = useState<Menu>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const toggleMenu = (m: Exclude<Menu, null>) => setOpen(open === m ? null : m);
  const toggle = (key: ListKey, v: string) => onChange({ ...value, [key]: toggled(value[key], v) });
  const anyActive =
    !!value.audience || value.when !== 'anytime' || LIST_KEYS.some((k) => value[k].length > 0);

  return (
    <div ref={wrapRef} className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <FilterButton
            label={
              value.when === 'anytime'
                ? t('activities:filters.when')
                : t(`home:filters.times.${value.when}`)
            }
            active={value.when !== 'anytime'}
            onClick={() => toggleMenu('when')}
          />
          {open === 'when' && (
            <OptionMenu
              options={o.when}
              selected={value.when}
              onSelect={(w: HomeTime) => {
                onChange({ ...value, when: w });
                setOpen(null);
              }}
            />
          )}
        </div>

        <div className="relative">
          <FilterButton
            label={value.audience ? o.ageLabel(value.audience) : t('activities:filters.age')}
            active={!!value.audience}
            onClick={() => toggleMenu('age')}
          />
          {open === 'age' && (
            <OptionMenu
              options={o.age}
              selected={value.audience ?? 'all'}
              onSelect={(a) => {
                onChange({ ...value, audience: a === 'all' ? null : (a as AudienceKey) });
                setOpen(null);
              }}
            />
          )}
        </div>

        {LIST_KEYS.map((key) => (
          <div key={key} className="relative">
            <FilterButton
              label={
                value[key].length > 0
                  ? `${o.listTitle[key]} · ${value[key].length}`
                  : o.listTitle[key]
              }
              active={value[key].length > 0}
              onClick={() => toggleMenu(key)}
            />
            {open === key && (
              <CheckboxMenu
                options={o.lists[key]}
                selected={value[key]}
                onToggle={(v) => toggle(key, v)}
              />
            )}
          </div>
        ))}

        {anyActive && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_PICKS_FILTERS)}
            className="text-sm text-primary font-semibold hover:underline"
          >
            {t('activities:filters.clearAll')}
          </button>
        )}
      </div>

      {anyActive && (
        <div className="flex flex-wrap items-center gap-2">
          {value.when !== 'anytime' && (
            <Chip
              label={t(`home:filters.times.${value.when}`)}
              onRemove={() => onChange({ ...value, when: 'anytime' })}
            />
          )}
          {value.audience && (
            <Chip
              label={o.ageLabel(value.audience)}
              onRemove={() => onChange({ ...value, audience: null })}
            />
          )}
          {LIST_KEYS.flatMap((key) =>
            value[key].map((v) => (
              <Chip
                key={`${key}-${v}`}
                label={o.lists[key].find((x) => x.value === v)?.label ?? v}
                onRemove={() => toggle(key, v)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Phone version: the same filters in a bottom sheet, applied with one button. */
export function PicksFiltersSheet({
  open,
  value,
  categories,
  onClose,
  onApply,
}: {
  open: boolean;
  value: PicksFilterState;
  categories: string[];
  onClose: () => void;
  onApply: (next: PicksFilterState) => void;
}) {
  const o = useOptions(categories);
  const { t } = o;
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <button type="button" aria-label="close" className="flex-1 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 flex items-center justify-between flex-shrink-0 relative">
          <button onClick={onClose} className="text-sm text-gray-500">
            {t('activities:filters.cancel')}
          </button>
          <div className="font-semibold text-sm">{t('activities:filters.mobileButtonNone')}</div>
          <button
            onClick={() => setDraft(EMPTY_PICKS_FILTERS)}
            className="text-sm text-primary font-semibold"
          >
            {t('activities:filters.clearAll')}
          </button>
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 bg-soft-gray rounded-full" />
        </div>
        <div className="overflow-y-auto px-5 py-4 flex-1 space-y-6">
          <RadioSection
            title={t('activities:filters.when')}
            name="pick-when"
            options={o.when}
            selected={draft.when}
            onSelect={(w: HomeTime) => setDraft((d) => ({ ...d, when: w }))}
          />
          <RadioSection
            title={t('activities:filters.age')}
            name="pick-age"
            options={o.age}
            selected={draft.audience ?? 'all'}
            onSelect={(a) =>
              setDraft((d) => ({ ...d, audience: a === 'all' ? null : (a as AudienceKey) }))
            }
          />
          {LIST_KEYS.map((key) => (
            <CheckboxSection
              key={key}
              title={o.listTitle[key]}
              options={o.lists[key]}
              selected={draft[key]}
              onToggle={(v) => setDraft((d) => ({ ...d, [key]: toggled(d[key], v) }))}
            />
          ))}
        </div>
        <div className="px-5 py-3 bg-white flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
            className="w-full bg-primary text-white rounded-full py-3 font-semibold text-sm"
          >
            {t('activities:filters.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
